import { CommonModule, DatePipe } from "@angular/common";
import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { catchError, debounceTime, distinctUntilChanged, from, map, merge, of, startWith, switchMap, take } from 'rxjs';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from "@angular/forms";
import { DateAdapter, MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MultilingualDateAdapter } from "../../../../../adapters/multilingual-date.adapter";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { CONSTANTS } from "../../../../../Constants/Constants";
import { TranslatePipe } from "@ngx-translate/core";
import { SolPropertiesService } from "../../../../../services/properties/sol-properties.service";
import { DropDownItem, EstimateStatus, MinimumProjectStandardMarginRequestBase } from "../../../../../models/dtos";
import { CommonFunctionsService } from "../../../../../services/common-functions.service";
import { ReportsCommonService } from "../../../../../services/reports/reports-common/reports-common.service";
import { IEstimateSettingFormGroup } from "../../../../../models/properties/IEstimateSettingFormGroup";
import { CommonDataService } from "../../../../../services/common-data.service";
import { ISubmittalPackages } from "../../../../../models/properties/ISubmittalPackages";
import { MultiPdfReportDropdownComponent } from '../../../../Common/multi-pdf-report-dropdown/multi-pdf-report-dropdown.component';
import { CULTURE_ID } from "../../../../../models/enum";
import { DecimalPrecisionPipe } from "../../../../../pipes/decimal-precision-pipe/decimal-precision.pipe";
import { SellAsMinimumMarginService } from "../../../../../services/sell-as-minimum-margin.service";
import { SolEstimateService } from "../../../../../services/estimate/sol-estimate.service";
import { EstimateCommonFunctionsService } from "../../../../../services/estimate/estimate-common-functions.service";
import { NumericFieldConfig } from "../../../../../shared/numeric/numeric.types";
import { NumericMaskDirective } from "../../../../../shared/numeric/numeric-mask.directive";

@Component({
    selector: 'app-sol-properties-estimate-settings',
    templateUrl: './sol-properties-estimate-settings.component.html',
    styleUrls: ['./sol-properties-estimate-settings.component.scss'],
    standalone: true,
    imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatCheckboxModule,
    MatDatepickerModule,
    TranslatePipe,
    MultiPdfReportDropdownComponent,
    DecimalPrecisionPipe,
    NumericMaskDirective
],
    providers: [{ provide: DateAdapter, useClass: MultilingualDateAdapter }, { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS }, DatePipe]
})
export class SolPropertiesEstimateSettingsComponent implements OnInit {

    estimateSettingsForm!: FormGroup<IEstimateSettingFormGroup>;
    estimateTypes: DropDownItem[] = [];
    verticalMarkets: DropDownItem[] = [];
    userTypes: DropDownItem[];
    submittalPackageData: ISubmittalPackages[] = [];
    businessName: string = CONSTANTS.EMPTY_STRING;
    isCreateNewEstimate: boolean = false;
    mechanicalFeeDisabled: boolean = false;
    isSalesTypeVisible: boolean = false;
    isGmPercentVisible: boolean = false;
    isGmPercentRateVisible: boolean = false;

    private readonly destroyRef = inject(DestroyRef);
    private initialGmPercent: number = CONSTANTS.ZERO;
    readonly gmPercentConfig: NumericFieldConfig = {
        kind: 'percentage',
        allowNegative: true,
        min: CONSTANTS.ESTIMATE_PROPERTIES_GM_PERCENT_MIN_VALUE,
        max: CONSTANTS.ESTIMATE_PROPERTIES_GM_PERCENT_MAX_VALUE,
        step: CONSTANTS.ZERO_POINT_ONE,
        scale: CONSTANTS.TWO,
        useGrouping: false,
        revertToLastValidValue: true
    };

    constructor(private readonly fb: NonNullableFormBuilder,
        private readonly solPropertiesService: SolPropertiesService,
        private readonly commonFunctionsService: CommonFunctionsService,
        private readonly reportsCommonService: ReportsCommonService,
        private readonly commonDataService: CommonDataService,
        private readonly sellAsMinimumMarginService: SellAsMinimumMarginService,
        private readonly solEstimateService: SolEstimateService,
        private readonly estimateCommonFunctionsService: EstimateCommonFunctionsService
    ) { }

    ngOnInit(): void {
        this.initializeForm();
        this.mapAllMasterData();
        this.subscribeFormValueChanges();
        this.isCreateNewEstimate = this.commonDataService.getEstimateData().baseEstimateId === CONSTANTS.DEFAULT_ESTIMATE_ID;
        this.mechanicalFeeDisabled = this.computeMechanicalFeeDisabled();
        this.cleanupHiddenSectionValues();
        this.setupReadOnlyMode();
    }

    get showCmmcAndVerticalSalesSection(): boolean {
        const cultureId = this.commonDataService.getEstimateCultureId();
        return cultureId !== CULTURE_ID.FR_CA && cultureId !== CULTURE_ID.EN_CA;
    }

    get showSubcontractorFeeSection(): boolean {
        return this.commonFunctionsService.isSdiApplicable();
    }

    get showMechanicalFeeSection(): boolean {
        return this.estimateCommonFunctionsService.isMechanicalFeeApplicable();
    }

    get minimumGmPercent(): number {
        return this.estimateSettingsForm.controls.minimumGmPercent.value;
    }

    /**
     * Cleans up form values for conditionally hidden sections to prevent stale data
     * from being serialized on save when visibility conditions are not met
     */
    private cleanupHiddenSectionValues(): void {
        // Reset subcontractor fee controls when section is not visible
        if (!this.showSubcontractorFeeSection) {
            this.estimateSettingsForm.controls.subContractorFee.setValue(false, { emitEvent: false });
            this.estimateSettingsForm.controls.includeSubContractorFeeInPpm.setValue(false, { emitEvent: false });
        }
    }

    private computeMechanicalFeeDisabled(): boolean {
        const isSapSubmitted = this.commonDataService.getEstimateData().estimateStatusId === EstimateStatus.Submitted;
        // Key spelling ("Disablity") matches the general configuration entry used by the desktop app.
        const configValue = this.commonFunctionsService.getGeneralConfigValue('EstimatePropertiesDisablityCheckMechFee');
        return configValue?.toLowerCase() === 'true' || isSapSubmitted;
    }


    private subscribeFormValueChanges(): void {
        this.estimateSettingsForm.valueChanges
            .pipe(take(CONSTANTS.ONE))
            .subscribe(
                () => this.solPropertiesService.markPropertiesChanged()
            );
    }

    private setupReadOnlyMode(): void {
        this.commonDataService.readOnlyMode$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(isReadOnly => {
                if (isReadOnly) {
                    this.estimateSettingsForm.disable({ emitEvent: false });
                } else {
                    this.estimateSettingsForm.enable({ emitEvent: false });
                    // ECS AND CMMC currently remains disabled.
                    this.estimateSettingsForm.controls.ecs.disable({ emitEvent: false });
                    this.estimateSettingsForm.controls.cmmc.disable({ emitEvent: false });
                    // Include In PPM flags are display-only: the incoming values are kept and saved unchanged.
                    this.estimateSettingsForm.controls.includeTechnologyFeeInPpm.disable({ emitEvent: false });
                    this.estimateSettingsForm.controls.includeSubContractorFeeInPpm.disable({ emitEvent: false });
                    this.estimateSettingsForm.controls.includeRiskInPpm.disable({ emitEvent: false });
                    this.estimateSettingsForm.controls.includeMechanicalFeeInPpm.disable({ emitEvent: false });
                    this.estimateSettingsForm.controls.includeProductivityReserveInPpm.disable({ emitEvent: false });
                    if (this.mechanicalFeeDisabled) {
                        this.estimateSettingsForm.controls.mechanicalFee.disable({ emitEvent: false });
                    }
                }
            });
    }

    private initializeForm(): void {
        const estimateSettings = this.solPropertiesService.getEstimateSettings();
        const estimateData = this.solPropertiesService.getEstimateData();
        this.solEstimateService.ensureModelPopulated();
        const isMarkupSelected = this.solEstimateService.getModel()?.markUpToggle ?? false;
        const calculatedSellAs = this.solEstimateService.getModel()?.sellAs?.calculated;
        const soldAsGmPercent = isMarkupSelected
            ? this.estimateCommonFunctionsService.getGrossMarginPercent(calculatedSellAs?.markUpPercentage ?? CONSTANTS.ZERO)
            : calculatedSellAs?.grossMarginPercentage ?? CONSTANTS.ZERO;
        this.initialGmPercent = soldAsGmPercent;
        const startDate = this.commonDataService.projectStartDateEndDate.startDate ? new Date(this.commonDataService.projectStartDateEndDate.startDate) : null;
        const endDate = this.commonDataService.projectStartDateEndDate.endDate ? new Date(this.commonDataService.projectStartDateEndDate.endDate) : null;
        const projectDuration = this.commonFunctionsService.calculateDurationDays(startDate, endDate);
        this.estimateSettingsForm = this.fb.group<IEstimateSettingFormGroup>({
            estimateName: this.fb.control<string>(estimateData.estimateName ?? CONSTANTS.EMPTY_STRING),
            estimateDescription: this.fb.control<string>(estimateData.description ?? CONSTANTS.EMPTY_STRING),
            estimateNumber: this.fb.control<string>(estimateData.estimateID ?? CONSTANTS.EMPTY_STRING),
            estimateType: this.fb.control<number>(estimateData.estimateTypeId ?? CONSTANTS.ONE),
            verticalMarket: this.fb.control<number>(estimateSettings.verticalMarketId),
            business: this.fb.control<string>(this.commonFunctionsService.getBusinessName(estimateData.business) ?? CONSTANTS.EMPTY_STRING),
            segment: this.fb.control<string>(this.solPropertiesService.getSegmentName(estimateData.profitCenterId)),
            salesType: this.fb.control<number>(estimateSettings.salesType ?? CONSTANTS.ONE),
            systemType: this.fb.control<number>(estimateSettings.systemType ?? CONSTANTS.ONE),
            gmPercent: this.fb.control<number>(soldAsGmPercent),
            minimumGmPercent: this.fb.control<number>(CONSTANTS.ZERO),
            jobStartDate: this.fb.control<Date | null>(startDate),
            durationDays: this.fb.control<number>(projectDuration),
            jobEndDate: this.fb.control<Date | null>(endDate),
            userType: this.fb.control<number>(estimateData.userTypeId ?? CONSTANTS.ONE),
            technologyFee: this.fb.control<boolean>(estimateSettings.useTechFee),
            includeTechnologyFeeInPpm: this.fb.control<boolean>(estimateSettings.includeTechFeeInPPM ?? false),
            subContractorFee: this.fb.control<boolean>(estimateSettings.useSubFee),
            includeSubContractorFeeInPpm: this.fb.control<boolean>(estimateSettings.includeSubFeeInPPM ?? false),
            risk: this.fb.control<boolean>(estimateSettings.useAutoRisk),
            includeRiskInPpm: this.fb.control<boolean>(estimateSettings.includeRiskInPPM ?? false),
            cmmc: this.fb.control<boolean>({ value: estimateSettings.isCUI, disabled: true }),
            ecs: this.fb.control<boolean>({ value: estimateData.isECS, disabled: true }),
            mechanicalFee: this.fb.control<boolean>(estimateSettings.useMechFeeUserInput ?? false),
            includeMechanicalFeeInPpm: this.fb.control<boolean>(estimateSettings.includeMechFeeInPPM ?? false),
            productivityReserve: this.fb.control<boolean>(estimateSettings.useProductivityReserve ?? false),
            includeProductivityReserveInPpm: this.fb.control<boolean>(estimateSettings.includeProductivityReserveInPPM ?? false),
            submittalPackage: this.fb.control<string>(estimateSettings.submittalPackageId ?? CONSTANTS.EMPTY_STRING)
        });
        this.setupMinimumMarginSync();
        this.registerFormChangeHandlers();
    }

    private setupMinimumMarginSync(): void {
        merge(
            this.estimateSettingsForm.controls.verticalMarket.valueChanges,
            this.estimateSettingsForm.controls.salesType.valueChanges,
            this.estimateSettingsForm.controls.systemType.valueChanges
        ).pipe(
            startWith(null),
            debounceTime(100),
            map(() => this.buildMinimumMarginRequest()),
            distinctUntilChanged((previousRequest, currentRequest) =>
                this.getMinimumMarginRequestKey(previousRequest) === this.getMinimumMarginRequestKey(currentRequest)
            ),
            switchMap(request => {
                if (!request) {
                    return of(CONSTANTS.ZERO);
                }

                const sellAsSellPrice = this.commonFunctionsService.toNumberOrNull(
                    this.solPropertiesService.getEstimateMetaDataModel()?.estimateTab?.soldASSellPrice
                );

                return from(this.sellAsMinimumMarginService.getMinimumMarginForRequest(request, sellAsSellPrice))
                    .pipe(catchError(() => of(CONSTANTS.ZERO)));
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(minimumMargin => this.applyMinimumMarginVisibility(minimumMargin));
    }

    private getMinimumMarginRequestKey(request: MinimumProjectStandardMarginRequestBase | null): string {
        if (!request) {
            return CONSTANTS.EMPTY_STRING;
        }

        return [
            request.cultureId,
            request.verticalMarketId,
            request.segmentId,
            request.subSegmentId,
            request.systemOrSalesTypeId
        ].join('|');
    }

    private buildMinimumMarginRequest(): MinimumProjectStandardMarginRequestBase | null {
        if (this.commonDataService.getEstimateId() === CONSTANTS.DEFAULT_ESTIMATE_ID) {
            return null;
        }

        const estimateSettings = this.solPropertiesService.getEstimateSettings();
        const estimateData = this.solPropertiesService.getEstimateData();

        return this.commonFunctionsService.buildMinimumProjectStandardMarginRequest(
            this.commonDataService.getEstimateCultureId(),
            this.estimateSettingsForm.controls.verticalMarket.value ?? CONSTANTS.ZERO,
            estimateData.profitCenterId ?? CONSTANTS.ZERO,
            estimateSettings.subSegmentId ?? CONSTANTS.ZERO,
            estimateSettings.businessUnitId ?? CONSTANTS.ZERO,
            this.estimateSettingsForm.controls.salesType.value ?? CONSTANTS.ZERO,
            this.estimateSettingsForm.controls.systemType.value ?? CONSTANTS.ZERO
        );
    }

    private applyMinimumMarginVisibility(minimumMargin: number): void {
        this.estimateSettingsForm.controls.minimumGmPercent.setValue(minimumMargin, { emitEvent: false });
        const isMinimumMarginAvailable = minimumMargin > CONSTANTS.ZERO_POINT_ZERO;
        const estimateSettings = this.solPropertiesService.getEstimateSettings();
        const isSalesTypeApplicable = this.commonFunctionsService.isSalesTypeApplicableForMinimumMargin(
            estimateSettings.businessUnitId,
            this.commonDataService.getEstimateCultureId()
        );

        this.isSalesTypeVisible = isSalesTypeApplicable && isMinimumMarginAvailable;
        this.isGmPercentVisible = !isSalesTypeApplicable && isMinimumMarginAvailable;
        this.isGmPercentRateVisible = isMinimumMarginAvailable;

        estimateSettings.isSalesTypeVisible = this.isSalesTypeVisible;
        estimateSettings.isSystemTypeVisible = this.isGmPercentVisible;
        this.updateSoldAsGrossMarginPercent();
    }

    private updateSoldAsGrossMarginPercent(): void {
        // The field is only seeded with the displayed Sell-As GM%. Writing that back untouched would be
        // reported as a user edit on OK and force Fixed Margin (e.g. after a tax type change).
        const gmPercent = this.estimateSettingsForm.controls.gmPercent.value;
        if (Number(gmPercent) === Number(this.initialGmPercent)) {
            return;
        }

        const grossMargin = this.solPropertiesService.getOtherMetaDataModel()?.grossMargin;
        if (grossMargin) {
            const isMarkupSelected = this.solEstimateService.getModel()?.markUpToggle ?? false;
            grossMargin.soldASGMPercent = isMarkupSelected
                ? this.estimateCommonFunctionsService.getMarkUpPercent(gmPercent)
                : gmPercent;
        }
    }

    /**
     * Keeps job start date, job end date and duration consistent.
     *
     * Driven by the committed value of each control (dateChange for the pickers, focus-out for the
     * duration) instead of valueChanges: the handler only ever rewrites the two fields the user did
     * not edit, so the three controls can't update each other in a loop, and typing a duration
     * doesn't move the end date on every keystroke.
     */
    onJobStartDateChange(): void {
        const { jobStartDate, jobEndDate, durationDays } = this.estimateSettingsForm.controls;

        if (!jobStartDate.value || !jobEndDate.value) {
            durationDays.setValue(CONSTANTS.ZERO);
            return;
        }

        // Start moved past the end — keep the duration by pushing the end date out from the new start.
        if (this.isDateBefore(jobEndDate.value, jobStartDate.value)) {
            jobEndDate.setValue(this.addDays(jobStartDate.value, durationDays.value));
            return;
        }

        durationDays.setValue(this.commonFunctionsService.calculateDurationDays(jobStartDate.value, jobEndDate.value));
    }

    onJobEndDateChange(): void {
        const { jobStartDate, jobEndDate, durationDays } = this.estimateSettingsForm.controls;

        if (!jobEndDate.value || !jobStartDate.value) {
            durationDays.setValue(CONSTANTS.ZERO);
            return;
        }

        // End moved before the start — keep the duration by pulling the start date back from the new end.
        if (this.isDateBefore(jobEndDate.value, jobStartDate.value)) {
            jobStartDate.setValue(this.addDays(jobEndDate.value, -durationDays.value));
            return;
        }

        durationDays.setValue(this.commonFunctionsService.calculateDurationDays(jobStartDate.value, jobEndDate.value));
    }

    onDurationDaysBlur(): void {
        const { jobStartDate, jobEndDate, durationDays } = this.estimateSettingsForm.controls;
        if (!durationDays.value || durationDays.value <= CONSTANTS.ZERO) {
            durationDays.setValue(CONSTANTS.ONE);
            return;
        }
        const sanitizedDuration = Math.max(CONSTANTS.ZERO, Number(durationDays.value) || CONSTANTS.ZERO);
        if (sanitizedDuration !== durationDays.value) {
            durationDays.setValue(sanitizedDuration);
        }

        if (!jobStartDate.value) {
            return;
        }

        jobEndDate.setValue(this.addDays(jobStartDate.value, sanitizedDuration));
    }

    private addDays(baseDate: Date, days: number): Date {
        const result = new Date(baseDate);
        result.setDate(result.getDate() + days);
        return result;
    }

    private isDateBefore(leftDate: Date, rightDate: Date): boolean {
        const left = new Date(leftDate).setHours(0, 0, 0, 0);
        const right = new Date(rightDate).setHours(0, 0, 0, 0);
        return left < right;
    }

    private registerFormChangeHandlers(): void {
        const estimateData = this.solPropertiesService.getEstimateData();
        const estimateSettings = this.solPropertiesService.getEstimateSettings();

        this.estimateSettingsForm.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                // Clean up values for hidden sections before syncing to model
                this.cleanupHiddenSectionValues();

                // Use getRawValue() for the full form snapshot because Angular excludes disabled
                // controls from form.value. Some properties on this screen can be disabled while
                // still needing to persist their current value, so reading every synced field from
                // rawValues keeps the model consistent and prevents hidden/disabled settings from
                // being dropped on reload.
                const rawValues = this.estimateSettingsForm.getRawValue();

                // Update EstimateData model
                estimateData.estimateName = rawValues.estimateName;
                estimateData.description = rawValues.estimateDescription;
                estimateData.estimateTypeId = rawValues.estimateType;
                estimateData.userTypeId = rawValues.userType;

                // Update EstimateSettings model
                estimateSettings.verticalMarketId = rawValues.verticalMarket;
                estimateSettings.salesType = rawValues.salesType;

                this.updateSoldAsGrossMarginPercent();
                estimateSettings.systemType = rawValues.systemType;
                estimateSettings.useTechFee = rawValues.technologyFee;
                estimateSettings.includeTechFeeInPPM = rawValues.includeTechnologyFeeInPpm;
                estimateSettings.useSubFee = rawValues.subContractorFee;
                estimateSettings.includeSubFeeInPPM = rawValues.includeSubContractorFeeInPpm;
                estimateSettings.useAutoRisk = rawValues.risk;
                estimateSettings.includeRiskInPPM = rawValues.includeRiskInPpm;
                estimateSettings.isCUI = rawValues.cmmc;
                // Hidden for non-Mechanical estimates: leave the stored value untouched.
                if (this.showMechanicalFeeSection) {
                    estimateSettings.useMechFeeUserInput = rawValues.mechanicalFee;
                    estimateSettings.includeMechFeeInPPM = rawValues.includeMechanicalFeeInPpm;
                }
                estimateSettings.useProductivityReserve = rawValues.productivityReserve;
                estimateSettings.includeProductivityReserveInPPM = rawValues.includeProductivityReserveInPpm;
                estimateSettings.submittalPackageId = this.getSubmittalPackagesValue(rawValues.submittalPackage);

                // Native mat-datepicker yields a local-midnight Date; anchor it to UTC midnight so the
                // selected calendar day is persisted without a timezone shift.
                const projectStartDate = this.commonFunctionsService.toUtcDate(rawValues.jobStartDate);
                const projectEndDate = this.commonFunctionsService.toUtcDate(rawValues.jobEndDate);

                // estimateData dates are consumed by the PPR dialog / SAP submittal and re-read when this form reloads
                estimateData.startDate = projectStartDate;
                estimateData.endDate = projectEndDate;
            });
    }


    private mapAllMasterData(): void {
        this.getEstimateTypes();
        this.getVerticalMarkets();
        this.getUserTypes();
        this.getSubmittalPackages();
    }


    getEstimateTypes() {
        this.estimateTypes = this.solPropertiesService.getEstimateTypesMasterData();
    }

    getVerticalMarkets() {
        this.verticalMarkets = this.solPropertiesService.getVerticalMarketsMasterData();
    }

    getUserTypes() {
        this.userTypes = this.solPropertiesService.getUserTypesMasterData();
    }
    getSubmittalPackagesValue(pkgId: string): string {
        return this.submittalPackageData.find(pkg => pkg.type === 'option' && pkg.id === pkgId)?.id ?? CONSTANTS.EMPTY_STRING;
    }

    getSubmittalPackages() {
        this.submittalPackageData = this.reportsCommonService.getMultiPdfDropdownItems();
        this.ensureSubmittalPackageSelection();
    }

    private ensureSubmittalPackageSelection(): void {
        const optionIds = this.submittalPackageData
            .filter(pkg => pkg.type === 'option' && !!pkg.id)
            .map(pkg => pkg.id as string);

        if (optionIds.length === CONSTANTS.ZERO) {
            return;
        }

        const currentValue = this.estimateSettingsForm.controls.submittalPackage.value;
        if (currentValue && optionIds.includes(currentValue)) {
            return;
        }

        const packageId = this.solPropertiesService.getEstimateSettings().submittalPackageId || this.commonFunctionsService.getSubmittalPackageFromPreferences();
        const fallbackValue = optionIds.find(id => id.toLowerCase() === packageId.toLowerCase())
            ?? optionIds[CONSTANTS.ZERO];

        this.estimateSettingsForm.controls.submittalPackage.setValue(fallbackValue, { emitEvent: false });
        this.solPropertiesService.getEstimateSettings().submittalPackageId = fallbackValue;
    }

    //#region Form Getters
    get estimateName() {
        return this.estimateSettingsForm.controls.estimateName as FormControl<string>;
    }
    get estimateNumber() {
        return this.estimateSettingsForm.controls.estimateNumber as FormControl<string>;
    }
    get business() {
        return this.estimateSettingsForm.controls.business as FormControl<string>;
    }

    get segment(){
        return this.estimateSettingsForm.controls.segment as FormControl<string>;
    }



    // logic

    onRiskSelectionChange(): void {
        const riskControl = this.estimateSettingsForm.controls.risk as FormControl<boolean>;
        const includeRiskInPpmControl = this.estimateSettingsForm.get('includeRiskInPpm') as FormControl<boolean>;

        if (!riskControl.value) {
            includeRiskInPpmControl.setValue(true);
        } else {
            includeRiskInPpmControl.setValue(false);
        }
    }


}
